(ns dre.pages.detail.utils
  (:require
   [cuerdas.core :as str]
   [clojure.math.combinatorics :as combo]
   [dre.coll :refer [assoc-when distinct-by index-of index-by find-first]] 
   [dre.common.document-util :refer [process-response article-id->group-id
                                     guid->group-id]]
   [re-frame.core :as rf]))

(defn- quick-process-nodes
  [nodes xml-subjects xml-guid-map]
  ;; matched-indices never used from xml-annotation, so not included below
  (loop [[node & nodes] nodes
         i 0
         id ""
         text ""
         tags (transient [])
         guid-map (if xml-guid-map
                    :keep-input-guid-map
                    (transient {}))
         highlights (transient [])
         report (transient [])
         text-by-nodes (transient [])]
    (if node
      (let [r (process-response
               nil
               {:subjects xml-subjects
                :guid-map xml-guid-map
                :annotation node
                :report? true})]
        (recur nodes
               (inc i)
               (.concat id (:id r))
               (.concat text (:text r))
               (reduce conj! tags (:tags r))
               (if (= guid-map :keep-input-guid-map)
                 guid-map
                 (reduce conj! guid-map (:guid-map r)))
               (reduce conj! highlights (map #(assoc % :node-id i) (:highlights r)))
               (reduce conj! report (:report r)) 
               (reduce conj! text-by-nodes [{:node-id i :text (:text r)}])))
      ;;done
      {:id id
       :text-by-nodes (persistent! text-by-nodes)
       :text text
       :tags (persistent! tags)
       :guid-map
       (if (= guid-map :keep-input-guid-map)
         xml-guid-map
         (persistent! guid-map))
       :highlights (persistent! highlights)
       :report (persistent! report)})))

(defn- process-article
  [article]
  (let [xml-annotation-response (-> article :xml-annotated :annotations)
        annotation (-> article :annotation)
        xml-subjects (get xml-annotation-response :subjects)
        xml-guid-map (get xml-annotation-response :guid-map)

        processed-annotation
        (when annotation
          (process-response
           nil
           {:annotation annotation
            :report? true}))]
    (assoc-when
     article
     :xml-annotation
     (->
      (quick-process-nodes
       (:document/nodes xml-annotation-response)
       xml-subjects
       xml-guid-map))

     :report
     processed-annotation)))

(defn transform-article-annotation
  [response]
  (let [article (:article response)
        processed-article (process-article article)]
    (-> response
        (assoc :article processed-article))))

;;

(defn get-pico
  [article pico]
  (let [results (:xml-annotation article)
        {:keys [report]} results
        subjects report]
    (sort-by #(vector (- (:count %))
                      (-> % :phrase first :label))
             (filter (fn [e] (some #(= % pico) (:pico e))) subjects))))

(defn article-context-pills
  [article]
  (let [i (take 5 (get-pico article :intervention))
        c (take 5 (get-pico article :characteristic))
        o (take 5 (get-pico article :outcome))
        m (take 5 (get-pico article :misc))
        terms (distinct (remove nil? (concat i c o m)))
        senses (map (fn [t] (:sense (first (:phrase t)))) terms)

        combinations (combo/combinations senses 3)]
    ;; TODO: fallback to free text title in-title in case no senses
    {:query/type "articles"
     :query/pills
     {:pill/type "or"
      :pill/pills
      (map (fn [[s1 s2 s3]]
             {:pill/type "and"
              :pill/pills
              [{:pill/query s1 :pill/type "term"}
               {:pill/query s2 :pill/type "term"}
               {:pill/query s3 :pill/type "term"}]})
           (take 4 combinations))}}))

;;

(defn plot-data [doc format-related-uri click-event]
  (let [relations (:article/relations doc)
        groups (:article/groups doc)]
    (when (seq relations)
      (let [current-guid (:article/guid doc)
            data relations
            data (distinct-by :target/guid data)
            data (mapv #(update % :target/published
                                (fn [d]
                                  (js/Date.parse d)))
                       data)
            ;; some WHO articles have date 1900...
            data (filter #(> (:target/published %)
                             (js/Date. 1920 0 1))
                         data)
            data (sort-by :target/published data)
            data (mapv identity data)
            data (reduce-kv
                  (fn [data _idx target]
                    (let [{:keys [:target/guid]} target
                          target-group (guid->group-id guid groups)
                          group-exists-idx (when target-group
                                             (index-of
                                              data
                                              (find-first #(= target-group (:target/group %)) data)))
                          data (cond
                                 (and target-group (not group-exists-idx))
                                 (conj data (assoc target :target/group  target-group :target/visible true))
                                 (and target-group group-exists-idx)
                                 (do
                                   (conj data (assoc target :target/group  target-group :target/visible false))
                                   (update-in data [group-exists-idx :target/neighbours]
                                              (comp distinct concat) (:target/neighbours target)))
                                 :else (conj data (assoc target :target/visible true)))] data))
                  [] data)
            data (map #(assoc % :target/group-str
                              (->> (get groups (:target/group %))
                                   (map :article/uri)
                                   (map format-related-uri)
                                   (str/join " | "))) data)
            data (let [visibles (filter :target/visible data)
                       group-targets (index-by :target/group visibles)]
                   (map
                    (fn [visible]
                      (let [neighbours (:target/neighbours visible)
                            neighbours (map
                                        #(if-let [sub (article-id->group-id
                                                       groups (:target/guid %))]
                                           (select-keys
                                            (get group-targets sub)
                                            [:target/uri :target/guid :target/source])
                                           %)
                                        neighbours)
                            neighbours (distinct neighbours)]
                        (assoc visible :target/neighbours neighbours)))
                    visibles))
            membership (fn [guid target]
                         (->> (:target/group target)
                              (get groups)
                              (map :article/guid)
                              (some #{guid})))
            icon (if (:related-base doc)
                   :location-dot
                   :thumbtack)
            mf (fn [rel]
                 (-> rel
                     (cond->
                      (= current-guid (:target/guid rel)) (assoc :beeswarm/icon icon)
                      (membership current-guid rel) (assoc :beeswarm/icon icon))
                     (assoc :beeswarm/class
                            (:target/category rel))))
            data (mapv mf data)
            indices (reduce
                     (fn [{:keys [rel-base-idx curr-idx]} [i rel]]
                       (let [new-rel-base-idx (or rel-base-idx
                                                  (when (= (:related-base doc) (:target/guid rel))
                                                    i)
                                                  (when (membership (:related-base doc) rel)
                                                    i))

                             new-curr-idx (or curr-idx
                                              (when (= current-guid (:target/guid rel))
                                                i)
                                              (when (membership current-guid rel)
                                                i))
                             new-val    {:rel-base-idx new-rel-base-idx
                                         :curr-idx new-curr-idx}]
                         (if (and new-rel-base-idx new-curr-idx)
                           (reduced new-val)
                           new-val)))
                     {} (map vector (range) data))
            data (if-let [rel-base-idx (:rel-base-idx indices)]
                   (update (vec data)
                           rel-base-idx
                           (fn [rel]
                             (assoc rel
                                    :beeswarm/icon
                                    :thumbtack)))
                   data)
            next (get data (inc (:curr-idx indices)))
            prev (get data (dec (:curr-idx indices)))
            related-base (or
                          (:related-base doc)
                          current-guid)
            click-handler #(let [new-guid (:target/guid %)
                                 group (:target/group %)]
                             (rf/dispatch [click-event
                                           current-guid
                                           related-base
                                           new-guid
                                           group]))
            legend-data (distinct
                         (map #(do {:beeswarm/label (:target/category %)
                                    :beeswarm/class (:target/category %)})
                              data))]
        {:legend-data   legend-data
         :data          data
         :next          next
         :click-handler click-handler
         :related-base  related-base
         :prev          prev}))))
